import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Config } from '../utils/Config';
import useLanguageCode from '../hooks/useLanguageCode';
import { useTranslation } from 'react-i18next';

function GlobeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.21 2.56 3.46 5.71 3.46 9S14.21 18.44 12 21c-2.21-2.56-3.46-5.71-3.46-9S9.79 5.56 12 3z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-secondary-dark" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function LanguageSelector() {
  const languageCode = useLanguageCode();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const onClickItem = useCallback(lang => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  }, [i18n]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-neutral hover:bg-gray-100 hover:text-black transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <GlobeIcon />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-gray-200 bg-white shadow-lg py-1.5 z-50 max-h-80 overflow-y-auto">
          {Object.keys(Config.languages).map(lang => (
            <button
              key={lang}
              onClick={() => onClickItem(lang)}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-secondary/40 transition-colors duration-100"
            >
              <span className="w-4 flex-shrink-0">
                {languageCode === lang && <CheckIcon />}
              </span>
              <span className={languageCode === lang ? 'font-medium text-black' : 'text-neutral'}>
                {Config.languages[lang]}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
